"use client"

import { useState, useEffect } from "react"
import { 
  Box, 
  Grid, 
  Paper, 
  CircularProgress, 
  Typography,
  Avatar,
  Stack,
  Container
} from "@mui/material"
import {
  AutoAwesome as SparklesIcon
} from "@mui/icons-material" 

import PriorityTasks from "../components/PriorityTask"
import SmartGoals from "../components/SmartGoals"
import ToDoList from "../components/ToDoList"
import Meetings from "../components/Meetings"
import OrganizationalEvents from "../components/Events"
import Notes from "../components/Notepad";
import PersonalizedCalendar from "./LandingPageCalender";
import EmployeeAttendanceCard from "../components/EmployeeAttendanceCard";
import EmployeePayslipCard from "../components/EmployeePayslipCard";
import ManagerTeamCompactCard from "../components/ManagerTeamCompactCard";
import DashboardTrainingCard from "../components/DashboardTrainingCard";
import gm from '../assets/gm.png'
import gf from '../assets/gf.png'
import ge from '../assets/ge.png'
import gn from '../assets/gn.png'
import nightOwl from '../assets/nightOwl.png'
import axios from "axios"

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000"

// Helper function to get greeting based on time of day
const getTimeBasedGreeting = () => {
  const currentHour = new Date().getHours();
  
  if (currentHour >= 5 && currentHour < 12) {
    return {
      greeting: "Good Morning",
      icon: "🌅",
      imagePath: gm,
      subGreeting: "Rise and shine! Ready for a productive day?"
    };
  } else if (currentHour >= 12 && currentHour < 17) {
    return {
      greeting: "Good Afternoon",
      icon: "☀️",
      imagePath: gf,
      subGreeting: "Keep up the great work! The day is still young."
    };
  } else if (currentHour >= 17 && currentHour < 21) {
    return {
      greeting: "Good Evening",
      icon: "🌙",
      imagePath: ge,
      subGreeting: "Time to wind down and reflect on today's achievements."
    };
  } else if (currentHour >= 21 || currentHour < 5) {
    if (currentHour >= 0 && currentHour < 5) {
      return {
        greeting: "Night Owl",
        icon: "🦉",
        imagePath: nightOwl,
        subGreeting: "Burning the midnight oil? You're dedicated!"
      };
    } else {
      return {
        greeting: "Good Night",
        icon: "🌙",
        imagePath: gn,
        subGreeting: "Rest well and recharge for tomorrow."
      };
    }
  }
  
  return {
    greeting: "Hello",
    icon: "👋",
    imagePath: "/images/greetings/hello.png",
    subGreeting: "Welcome to your dashboard!"
  };
};

export default function DashboardHome() {
  const [userRole, setUserRole] = useState("")
  const [loading, setLoading] = useState(true)
  const [employeeData, setEmployeeData] = useState({
    employee_id: "",
    full_name: "",
    phone_number: "",
    company_email: "",
    personalPhoto: null,
  })
  const [animateIn, setAnimateIn] = useState(false)
  const [greeting, setGreeting] = useState(getTimeBasedGreeting())

  useEffect(() => {
    fetchEmployeeData()
    
    const interval = setInterval(() => {
      setGreeting(getTimeBasedGreeting())
    }, 60000)
    
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    if (!loading) {
      setTimeout(() => setAnimateIn(true), 100)
    }
  }, [loading])

  const fetchEmployeeData = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('token')
      
      if (!token) {
        setLoading(false)
        return
      }

      const payload = JSON.parse(atob(token.split('.')[1]))
      const company_id = payload.company_id
      const role = localStorage.getItem("role")?.toLowerCase()
      setUserRole(role || "employee")

      const response = await axios.get(`${API_URL}/api/employee/employeeprofile?company_id=${company_id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        withCredentials: true,
      })

      if (response.data.success) {
        const employeeInfo = {
          employee_id: response.data.data.employee_id,
full_name: `${response.data.data.firstName ?? ""} ${response.data.data.lastName ?? ""}`.trim(),
          phone_number: response.data.data.phone_number,
          company_email: response.data.data.company_email,
          personalPhoto: response.data.data.personalPhoto,
        }
        setEmployeeData(employeeInfo)
      }
    } catch (error) {
      //console.error("Error fetching employee data:", error)
      const role = localStorage.getItem("role")?.toLowerCase()
      setUserRole(role || "employee")
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #dbeafe 0%, #fae8ff 50%, #fce7f3 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box sx={{ position: "relative" }}>
          <CircularProgress
            size={80}
            thickness={4}
            sx={{
              color: "#a855f7",
            }}
          />
          <SparklesIcon
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              color: "#c084fc",
              fontSize: 32,
            }}
          />
        </Box>
      </Box>
    )
  }

  const animatedCardStyle = {
    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
    opacity: animateIn ? 1 : 0,
    transform: animateIn ? 'translateY(0)' : 'translateY(20px)',
  }

  const paperStyles = {
    p: 3,
    bgcolor: "#ffffff",
    borderRadius: 3,
    position: "relative",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
      transform: "translateY(-4px)",
    }
  }

  // Check if user is non-employee (manager, hr, finance, admin, etc.)
  const isNonEmployee = userRole !== "employee";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        position: "relative",
        overflow: "auto",
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
      }}
    >
      <Container maxWidth="xl" sx={{ position: "relative", zIndex: 10, py: 3 }}>
        {/* Hero Section with Greeting and Employee Name */}
        <Box
          sx={{
            mb: 6,
            ...animatedCardStyle,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
            gap: 4,
            borderRadius: 4,
            background: '#fff',
            transition: '0.3s ease',
            '&:hover': {
              transform: 'scale(1.03)',
            },
          }}
        >
          <Box sx={{ flex: 1, p: 3 }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                background: "linear-gradient(90deg, #9333ea 0%, #ec4899 50%, #3b82f6 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                fontSize: { xs: "1.75rem", sm: "2.5rem", md: "3rem" },
              }}
            >
              {greeting.greeting}, {employeeData?.full_name || "User"}!
            </Typography>

            <Typography
              variant="subtitle1"
              sx={{
                color: "#6b7280",
                mt: 1,
                fontSize: { xs: "0.875rem", sm: "1rem" },
              }}
            >
              {greeting.subGreeting}
            </Typography>
          </Box>

          <Box
            sx={{
              width: { xs: 280, sm: 420, md: 460 },
              height: { xs: 180, sm: 220, md: 260 },
              borderRadius: 4,
              overflow: 'hidden',
              ml: 20,
              flexShrink: 0,
            }}
          >
            <img
              src={greeting.imagePath}
              alt={greeting.greeting}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          </Box>
        </Box>

        {/* Main Dashboard Grid */}
        <Grid container spacing={3}>
          {userRole === "employee" ? (
            <>
              {/* EMPLOYEE LAYOUT - 3 cards in first row */}
              <Grid item xs={12} lg={4}>
                <Box sx={animatedCardStyle}>
                  <Paper
                    elevation={0}
                    sx={{
                      ...paperStyles,
                      border: "2px solid #bfdbfe",
                      boxShadow: "0 4px 16px rgba(191, 219, 254, 0.3)",
                      "&:hover": {
                        boxShadow: "0 12px 32px rgba(147, 197, 253, 0.4)",
                        borderColor: "#93c5fd",
                      },
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "4px",
                        background: "linear-gradient(90deg, #bfdbfe 0%, #93c5fd 100%)",
                        borderTopLeftRadius: "12px",
                        borderTopRightRadius: "12px",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        height: "100%",
                        overflow: "auto",
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                        '&::-webkit-scrollbar': {
                          display: 'none',
                        },
                      }}
                    >
                      <PriorityTasks />
                    </Box>
                  </Paper>
                </Box>
              </Grid>

              <Grid item xs={12} lg={4}>
                <Box sx={{ ...animatedCardStyle, transitionDelay: '100ms' }}>
                  <Paper
                    elevation={0}
                    sx={{
                      ...paperStyles,
                      border: "2px solid #bbf7d0",
                      boxShadow: "0 4px 16px rgba(187, 247, 208, 0.3)",
                      "&:hover": {
                        boxShadow: "0 12px 32px rgba(134, 239, 172, 0.4)",
                        borderColor: "#86efac",
                      },
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "4px",
                        background: "linear-gradient(90deg, #bbf7d0 0%, #86efac 100%)",
                        borderTopLeftRadius: "12px",
                        borderTopRightRadius: "12px",
                      },
                    }}
                  >
                    <SmartGoals />
                  </Paper>
                </Box>
              </Grid>

              <Grid item xs={12} lg={4}>
                <Box sx={{ ...animatedCardStyle, transitionDelay: '200ms' }}>
                  <Paper
                    elevation={0}
                    sx={{
                      ...paperStyles,
                      border: "2px solid #fbcfe8",
                      boxShadow: "0 4px 16px rgba(251, 207, 232, 0.3)",
                      "&:hover": {
                        boxShadow: "0 12px 32px rgba(249, 168, 212, 0.4)",
                        borderColor: "#f9a8d4",
                      },
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "4px",
                        background: "linear-gradient(90deg, #fbcfe8 0%, #f9a8d4 100%)",
                        borderTopLeftRadius: "12px",
                        borderTopRightRadius: "12px",
                      },
                    }}
                  >
                    <Meetings />
                  </Paper>
                </Box>
              </Grid>

              {/* Rest of employee layout remains the same */}
              <Grid item xs={12}>
                <Box sx={{ ...animatedCardStyle, transitionDelay: '300ms' }}>
                  <Paper
                    elevation={0}
                    sx={{
                      ...paperStyles,
                      border: "2px solid #e9d5ff",
                      boxShadow: "0 4px 16px rgba(233, 213, 255, 0.3)",
                      "&:hover": {
                        boxShadow: "0 12px 32px rgba(216, 180, 254, 0.4)",
                        borderColor: "#d8b4fe",
                      },
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "4px",
                        background: "linear-gradient(90deg, #e9d5ff 0%, #d8b4fe 100%)",
                        borderTopLeftRadius: "12px",
                        borderTopRightRadius: "12px",
                      },
                    }}
                  >
                    <ToDoList />
                  </Paper>
                </Box>
              </Grid>

              <Grid item xs={12} lg={4}>
                <Box sx={{ ...animatedCardStyle, transitionDelay: '400ms' }}>
                  <EmployeeAttendanceCard />
                </Box>
              </Grid>

              <Grid item xs={12} lg={4}>
                <Box sx={{ ...animatedCardStyle, transitionDelay: '500ms' }}>
                  <EmployeePayslipCard />
                </Box>
              </Grid>

              <Grid item xs={12} lg={4}>
                <Box sx={{ ...animatedCardStyle, transitionDelay: '600ms' }}>
                  <Paper
                    elevation={0}
                    sx={{
                      ...paperStyles,
                      border: "2px solid #a7f3d0",
                      boxShadow: "0 4px 16px rgba(167, 243, 208, 0.3)",
                      height: "320px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      "&:hover": {
                        boxShadow: "0 12px 32px rgba(52, 211, 153, 0.4)",
                        borderColor: "#34d399",
                      },
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "4px",
                        background: "linear-gradient(90deg, #34d399 0%, #059669 100%)",
                        borderTopLeftRadius: "12px",
                        borderTopRightRadius: "12px",
                      },
                    }}
                  >
                    <DashboardTrainingCard employeeId={employeeData.employee_id} />
                  </Paper>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ ...animatedCardStyle, transitionDelay: '700ms' }}>
                  <Paper
                    elevation={0}
                    sx={{
                      ...paperStyles,
                      border: "2px solid #fed7aa",
                      boxShadow: "0 4px 16px rgba(254, 215, 170, 0.3)",
                      "&:hover": {
                        boxShadow: "0 12px 32px rgba(253, 186, 116, 0.4)",
                        borderColor: "#fdba74",
                      },
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "4px",
                        background: "linear-gradient(90deg, #fed7aa 0%, #fdba74 100%)",
                        borderTopLeftRadius: "12px",
                        borderTopRightRadius: "12px",
                      },
                    }}
                  >
                    <OrganizationalEvents />
                  </Paper>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ ...animatedCardStyle, transitionDelay: '800ms' }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1,
                      bgcolor: "#ffffff",
                      borderRadius: 3,
                      border: "2px solid #fde68a",
                      height: "700px",
                      position: "relative",
                      boxShadow: "0 4px 16px rgba(253, 230, 138, 0.3)",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        boxShadow: "0 12px 32px rgba(252, 211, 77, 0.4)",
                        transform: "translateY(-4px)",
                        borderColor: "#fcd34d",
                      },
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "4px",
                        background: "linear-gradient(90deg, #fde68a 0%, #fcd34d 100%)",
                        borderTopLeftRadius: "12px",
                        borderTopRightRadius: "12px",
                      },
                    }}
                  >
                    <Box 
                      sx={{ 
                        height: "100%",
                        overflow: "hidden",
                        '& .MuiPaper-root': {
                          boxShadow: 'none',
                          border: 'none',
                          p: 0,
                          m: 0
                        }
                      }}
                    >
                      <PersonalizedCalendar />
                    </Box>
                  </Paper>
                </Box>
              </Grid>
            </>
          ) : (
            <>
              {/* NON-EMPLOYEE LAYOUT (Manager/HR/Finance/Admin) - 3 cards in first row for manager, 2 for others */}
              
              {/* Check if user is manager to show 3 cards in first row */}
              {userRole === "manager" ? (
                <>
                  {/* Meetings Card */}
                  <Grid item xs={12} md={4}>
                    <Box sx={animatedCardStyle}>
                      <Paper
                        elevation={0}
                        sx={{
                          ...paperStyles,
                          border: "2px solid #fbcfe8",
                          boxShadow: "0 4px 16px rgba(251, 207, 232, 0.3)",
                          "&:hover": {
                            boxShadow: "0 12px 32px rgba(249, 168, 212, 0.4)",
                            borderColor: "#f9a8d4",
                          },
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            height: "4px",
                            background: "linear-gradient(90deg, #fbcfe8 0%, #f9a8d4 100%)",
                            borderTopLeftRadius: "12px",
                            borderTopRightRadius: "12px",
                          },
                        }}
                      >
                        <Meetings />
                      </Paper>
                    </Box>
                  </Grid>

                  {/* Notes Card */}
                  <Grid item xs={12} md={4}>
                    <Box sx={{ ...animatedCardStyle, transitionDelay: '100ms' }}>
                      <Paper
                        elevation={0}
                        sx={{
                          ...paperStyles,
                          border: "2px solid #fde68a",
                          boxShadow: "0 4px 16px rgba(253, 230, 138, 0.3)",
                          "&:hover": {
                            boxShadow: "0 12px 32px rgba(252, 211, 77, 0.4)",
                            borderColor: "#fcd34d",
                          },
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            height: "4px",
                            background: "linear-gradient(90deg, #fde68a 0%, #fcd34d 100%)",
                            borderTopLeftRadius: "12px",
                            borderTopRightRadius: "12px",
                          },
                        }}
                      >
                        <Box 
                          sx={{ 
                            height: "100%",
                            overflow: "hidden",
                            '& .MuiPaper-root': {
                              boxShadow: 'none',
                              border: 'none',
                              p: 0,
                              m: 0
                            }
                          }}
                        >
                          <Notes />
                        </Box>
                      </Paper>
                    </Box>
                  </Grid>

                  {/* Team Today Card (ManagerTeamCompactCard) */}
                  <Grid item xs={12} md={4}>
                    <Box sx={{ ...animatedCardStyle, transitionDelay: '200ms' }}>
                      <Paper
                        elevation={0}
                        sx={{
                          ...paperStyles,
                          border: "2px solid #bbf7d0",
                          boxShadow: "0 4px 16px rgba(187, 247, 208, 0.3)",
                          "&:hover": {
                            boxShadow: "0 12px 32px rgba(134, 239, 172, 0.4)",
                            borderColor: "#86efac",
                          },
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            height: "4px",
                            background: "linear-gradient(90deg, #bbf7d0 0%, #86efac 100%)",
                            borderTopLeftRadius: "12px",
                            borderTopRightRadius: "12px",
                          },
                        }}
                      >
                        <ManagerTeamCompactCard />
                      </Paper>
                    </Box>
                  </Grid>
                </>
              ) : (
                <>
                  {/* For HR/Finance/Admin - 2 cards in first row */}
                  <Grid item xs={12} md={6}>
                    <Box sx={animatedCardStyle}>
                      <Paper
                        elevation={0}
                        sx={{
                          ...paperStyles,
                          border: "2px solid #fbcfe8",
                          boxShadow: "0 4px 16px rgba(251, 207, 232, 0.3)",
                          "&:hover": {
                            boxShadow: "0 12px 32px rgba(249, 168, 212, 0.4)",
                            borderColor: "#f9a8d4",
                          },
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            height: "4px",
                            background: "linear-gradient(90deg, #fbcfe8 0%, #f9a8d4 100%)",
                            borderTopLeftRadius: "12px",
                            borderTopRightRadius: "12px",
                          },
                        }}
                      >
                        <Meetings />
                      </Paper>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box sx={{ ...animatedCardStyle, transitionDelay: '100ms' }}>
                      <Paper
                        elevation={0}
                        sx={{
                          ...paperStyles,
                          border: "2px solid #fde68a",
                          boxShadow: "0 4px 16px rgba(253, 230, 138, 0.3)",
                          "&:hover": {
                            boxShadow: "0 12px 32px rgba(252, 211, 77, 0.4)",
                            borderColor: "#fcd34d",
                          },
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            height: "4px",
                            background: "linear-gradient(90deg, #fde68a 0%, #fcd34d 100%)",
                            borderTopLeftRadius: "12px",
                            borderTopRightRadius: "12px",
                          },
                        }}
                      >
                        <Box 
                          sx={{ 
                            height: "100%",
                            overflow: "hidden",
                            '& .MuiPaper-root': {
                              boxShadow: 'none',
                              border: 'none',
                              p: 0,
                              m: 0
                            }
                          }}
                        >
                          <Notes />
                        </Box>
                      </Paper>
                    </Box>
                  </Grid>
                </>
              )}

              {/* ToDoList (full width) */}
              <Grid item xs={12}>
                <Box sx={{ ...animatedCardStyle, transitionDelay: userRole === "manager" ? '300ms' : '200ms' }}>
                  <Paper
                    elevation={0}
                    sx={{
                      ...paperStyles,
                      border: "2px solid #e9d5ff",
                      boxShadow: "0 4px 16px rgba(233, 213, 255, 0.3)",
                      "&:hover": {
                        boxShadow: "0 12px 32px rgba(216, 180, 254, 0.4)",
                        borderColor: "#d8b4fe",
                      },
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "4px",
                        background: "linear-gradient(90deg, #e9d5ff 0%, #d8b4fe 100%)",
                        borderTopLeftRadius: "12px",
                        borderTopRightRadius: "12px",
                      },
                    }}
                  >
                    <ToDoList />
                  </Paper>
                </Box>
              </Grid>

              {/* Events and Calendar */}
              <Grid item xs={12} md={6}>
                <Box sx={{ ...animatedCardStyle, transitionDelay: userRole === "manager" ? '400ms' : '300ms' }}>
                  <Paper
                    elevation={0}
                    sx={{
                      ...paperStyles,
                      border: "2px solid #fed7aa",
                      boxShadow: "0 4px 16px rgba(254, 215, 170, 0.3)",
                      "&:hover": {
                        boxShadow: "0 12px 32px rgba(253, 186, 116, 0.4)",
                        borderColor: "#fdba74",
                      },
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "4px",
                        background: "linear-gradient(90deg, #fed7aa 0%, #fdba74 100%)",
                        borderTopLeftRadius: "12px",
                        borderTopRightRadius: "12px",
                      },
                    }}
                  >
                    <OrganizationalEvents />
                  </Paper>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ ...animatedCardStyle, transitionDelay: userRole === "manager" ? '500ms' : '400ms' }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1,
                      bgcolor: "#ffffff",
                      borderRadius: 3,
                      border: "2px solid #7087b6ff",
                      height: "700px",
                      position: "relative",
                      boxShadow: "0 4px 16px rgba(187, 221, 247, 0.3)",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        boxShadow: "0 12px 32px rgba(134, 209, 239, 0.4)",
                        transform: "translateY(-4px)",
                        borderColor: "#86d3efff",
                      },
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "4px",
                        background: "linear-gradient(90deg, #bbeff7ff 0%, #86cfefff 100%)",
                        borderTopLeftRadius: "12px",
                        borderTopRightRadius: "12px",
                      },
                    }}
                  >
                    <PersonalizedCalendar />
                  </Paper>
                </Box>
              </Grid>
            </>
          )}
        </Grid>
      </Container>
    </Box>
  )
}